import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  EmailAuthProvider,
  reauthenticateWithCredential,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { auth } from "@/lib/firebase";
import { registerPush, unregisterPush } from "@/lib/push";
import { queryClient } from "@/lib/queryClient";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Tek sefer kurulan axios interceptor'ları: isteğe token ekler, 401'de token'ı
// zorla yenileyip isteği bir kez tekrarlar.
let _interceptorsReady = false;
function ensureAxiosInterceptors() {
  if (_interceptorsReady) return;
  _interceptorsReady = true;

  // İstek: güncel Firebase ID token'ını ekle (token otomatik yenilenir).
  axios.interceptors.request.use(async (config) => {
    const u = auth.currentUser;
    if (u) {
      try {
        const token = await u.getIdToken();
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      } catch (_) {
        /* token alınamadı — istek yetkisiz gider */
      }
    }
    return config;
  });

  // Yanıt: 401 → token'ı ZORLA yenile ve isteği TEK kez tekrarla. Bu, oturum
  // ortasında dolan token'ı ve "token henüz hazır değilken atılan istek" yarışını
  // (F6) kurtarır. Token gerçekten geçersizse (iptal/devre dışı) oturumu kapatır;
  // ağ kaynaklı 401'de oturum KAPANMAZ (yanlış logout'u önlemek için).
  axios.interceptors.response.use(
    (r) => r,
    async (error) => {
      const cfg = error.config;
      const status = error?.response?.status;
      if (status === 401 && auth.currentUser && cfg && !cfg._retry) {
        cfg._retry = true;
        let fresh;
        try {
          fresh = await auth.currentUser.getIdToken(true);
        } catch (refreshErr) {
          if (refreshErr?.code && refreshErr.code !== "auth/network-request-failed") {
            try { await signOut(auth); } catch (_) {}
          }
          return Promise.reject(error);
        }
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${fresh}`;
        return axios(cfg);
      }
      return Promise.reject(error);
    }
  );
}

// GET /auth/me'yi getir; GEÇİCİ hatalarda (5xx / ağ / cold-start) kısa backoff
// ile birkaç kez dener. 404 = profil yok → hemen `noProfile` ile fırlatır
// (retry anlamsız). Tüm denemeler tükenirse `transient` hatası fırlatır.
async function fetchMyProfile() {
  let lastErr;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await axios.get(`${API}/auth/me`);
      return res.data;
    } catch (err) {
      lastErr = err;
      if (err?.response?.status === 404) {
        const e = new Error("no-profile");
        e.noProfile = true;
        throw e;
      }
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }
  }
  const e = new Error("profile-unreachable");
  e.transient = true;
  e.cause = lastErr;
  throw e;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [fbUser, setFbUser] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  // "transient" → Firebase oturumu var ama profil sunucudan alınamadı (geçici).
  // Bu durumda kullanıcıyı login'e ATMA; "tekrar dene" göster (bkz. ProtectedRoute).
  const [authError, setAuthError] = useState(null);
  // login/register akışları profili kendileri yükler; listener'ın aynı anda
  // ikinci bir /auth/me atmasını (F4 çift-çağrı yarışı) bu ref ile susturur.
  const authActionRef = useRef(false);

  // Firebase oturum durumunu dinle; oturum varsa Mongo profilini yükle.
  useEffect(() => {
    ensureAxiosInterceptors();
    const unsub = onAuthStateChanged(auth, async (u) => {
      setFbUser(u || null);
      setEmailVerified(!!(u && u.emailVerified));
      if (authActionRef.current) return; // login/register profili kendisi yükler
      if (!u) {
        setUser(null);
        setAuthError(null);
        setLoading(false);
        return;
      }
      try {
        const profile = await fetchMyProfile();
        setUser(profile);
        setAuthError(null);
      } catch (err) {
        if (err?.noProfile) {
          setUser(null); // gerçekten profil yok (404) — kaydı tamamlaması gerekir
          setAuthError(null);
        } else {
          // Geçici/erişilemez (5xx/ağ/cold-start): oturumu düşürme, hatayı işaretle.
          setAuthError("transient");
        }
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  // Giriş yapılınca cihazı push bildirimlerine kaydet (yalnız native; best-effort).
  useEffect(() => {
    if (user) registerPush();
  }, [user]);

  // Firebase oturumu varken profili yeniden dener (ProtectedRoute "tekrar dene").
  const reloadProfile = useCallback(async () => {
    if (!auth.currentUser) {
      setUser(null);
      setAuthError(null);
      return;
    }
    setLoading(true);
    try {
      const profile = await fetchMyProfile();
      setUser(profile);
      setAuthError(null);
    } catch (err) {
      if (err?.noProfile) {
        setUser(null);
        setAuthError(null);
      } else {
        setAuthError("transient");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    authActionRef.current = true; // listener'ın ikinci /auth/me'sini sustur (F4)
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setFbUser(auth.currentUser);
      setEmailVerified(!!auth.currentUser?.emailVerified);
      let profile;
      try {
        profile = await fetchMyProfile();
      } catch (err) {
        // Kimlik doğru; sorun profil getirme. "Şifre hatalı" DEME (F2).
        if (err?.noProfile) {
          throw new Error("Hesabınız için profil bulunamadı. Lütfen kaydınızı tamamlayın.");
        }
        throw new Error("Giriş yapıldı ancak sunucuya ulaşılamadı. Lütfen birkaç saniye sonra tekrar deneyin.");
      }
      setUser(profile);
      setAuthError(null);
      return profile;
    } finally {
      authActionRef.current = false;
    }
  }, []);

  // Google ile giriş. Mevcut kullanıcı (profili olan) → giriş yapılır ve
  // {status:"existing", user} döner. Yeni kullanıcı (profil yok) → oturum AÇIK
  // bırakılır ve {status:"new"} döner; çağıran "Kaydı tamamla" adımına yönlendirir.
  const signInWithGoogle = useCallback(async () => {
    authActionRef.current = true;
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      setFbUser(auth.currentUser);
      setEmailVerified(!!auth.currentUser?.emailVerified);
      try {
        const profile = await fetchMyProfile();
        setUser(profile);
        setAuthError(null);
        return { status: "existing", user: profile };
      } catch (err) {
        if (err?.noProfile) return { status: "new" };
        throw err; // geçici hata → yukarı taşı
      }
    } finally {
      authActionRef.current = false;
    }
  }, []);

  // Sosyal (Google/Apple) ile giriş yapmış ama profili olmayan kullanıcının
  // profilini oluşturur. Kimlik Firebase token'ından gelir; parola gerekmez.
  const completeSocialRegistration = useCallback(async (payload) => {
    if (!auth.currentUser) throw new Error("Önce Google ile giriş yapın.");
    authActionRef.current = true;
    try {
      const res = await axios.post(`${API}/auth/register`, payload);
      setUser(res.data.user);
      setAuthError(null);
      return res.data.user;
    } finally {
      authActionRef.current = false;
    }
  }, []);

  const register = useCallback(async (payload) => {
    const { email, sifre, ...rest } = payload;
    const profile = {
      ad_soyad: rest.ad_soyad,
      telefon: rest.telefon,
      role: rest.role,
      kvkk_onay: rest.kvkk_onay,
      ...(rest.bolgeler ? { bolgeler: rest.bolgeler } : {}),
      ...(rest.uzmanliklar ? { uzmanliklar: rest.uzmanliklar } : {}),
    };
    authActionRef.current = true;
    try {
      let cred;
      try {
        cred = await createUserWithEmailAndPassword(auth, email, sifre);
      } catch (err) {
        // Zombi kurtarma (F5): e-posta zaten Firebase'de kayıtlı. Bu, önceki yarım
        // kalmış bir kayıttan kalan PROFİLSİZ hesap olabilir. Verilen şifreyle giriş
        // yapmayı dene (sahiplik kanıtı — şifre yanlışsa burada patlar); profil yoksa
        // tamamla, profil zaten varsa kullanıcıyı giriş yapmış say.
        if (err?.code === "auth/email-already-in-use") {
          await signInWithEmailAndPassword(auth, email, sifre);
          let existing = null;
          try {
            existing = await fetchMyProfile();
          } catch (meErr) {
            if (!meErr?.noProfile) throw meErr; // geçici hata → yukarı taşı
          }
          if (existing) {
            setUser(existing);
            setAuthError(null);
            return existing; // zaten kayıtlı → giriş yapmış gibi devam
          }
          const recovered = await axios.post(`${API}/auth/register`, profile);
          setUser(recovered.data.user);
          setAuthError(null);
          return recovered.data.user;
        }
        throw err;
      }
      try {
        // PAK markalı doğrulama maili (backend → Resend). cred.user token'ı
        // interceptor tarafından eklenir.
        await axios.post(`${API}/auth/send-verification`);
      } catch (_) {
        /* doğrulama e-postası gönderilemedi — kayıt yine de tamamlanır */
      }
      // Profil POST'unu geçici hatalara karşı birkaç kez dene. Firebase kullanıcısı
      // oluştu ama profil oluşmazsa "zombie" (profilsiz, hiçbir şey yapamayan giriş)
      // kalmasın diye: kalıcı (4xx) hata veya tüm denemeler tükenince yeni Firebase
      // hesabını geri al, böylece kullanıcı temiz şekilde tekrar kaydolabilir (F5).
      let res;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          res = await axios.post(`${API}/auth/register`, profile);
          break;
        } catch (err) {
          const status = err?.response?.status;
          const permanent = status && status >= 400 && status < 500;
          if (permanent || attempt === 2) {
            try {
              await cred.user.delete(); // zombie hesabı temizle
            } catch (_) {
              /* geri alma başarısız (nadir) — kullanıcı sonra tekrar deneyebilir */
            }
            throw err;
          }
          await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
        }
      }
      setUser(res.data.user);
      setAuthError(null);
      return res.data.user;
    } finally {
      authActionRef.current = false;
    }
  }, []);

  const logout = useCallback(async () => {
    await unregisterPush(); // signOut'tan ÖNCE — silme isteği auth ister
    await signOut(auth);
    setUser(null);
    setAuthError(null);
    queryClient.clear(); // önceki kullanıcının özel verisi cache'te kalmasın (F8)
  }, []);

  const resetPassword = useCallback(async (email) => {
    // PAK markalı şifre sıfırlama maili (backend → Resend).
    await axios.post(`${API}/auth/send-password-reset`, { email });
  }, []);

  const resendVerification = useCallback(async () => {
    if (!auth.currentUser) return;
    await axios.post(`${API}/auth/send-verification`);
  }, []);

  // Re-check verification after the user clicks the e-mail link. Also mints a
  // fresh ID token so the backend sees the updated email_verified claim.
  const refreshVerification = useCallback(async () => {
    if (!auth.currentUser) return false;
    await auth.currentUser.reload();
    const verified = !!auth.currentUser.emailVerified;
    if (verified) {
      try { await auth.currentUser.getIdToken(true); } catch (_) { /* ignore */ }
    }
    setEmailVerified(verified);
    return verified;
  }, []);

  // Hesabı kalıcı olarak sil: hassas işlem olduğu için önce şifreyle yeniden
  // kimlik doğrula, sonra backend veriyi temizleyip Firebase kullanıcısını siler.
  const deleteAccount = useCallback(async (password) => {
    const u = auth.currentUser;
    if (!u) throw new Error("Oturum bulunamadı.");
    const cred = EmailAuthProvider.credential(u.email, password);
    await reauthenticateWithCredential(u, cred);
    await axios.delete(`${API}/me`);
    await signOut(auth).catch(() => {});
    setUser(null);
    setAuthError(null);
    queryClient.clear();
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, fbUser, emailVerified, loading, authError, reloadProfile, login, register, signInWithGoogle, completeSocialRegistration, logout, resetPassword, resendVerification, refreshVerification, deleteAccount }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function formatApiError(detail) {
  if (detail == null) return "Bir hata oluştu. Lütfen tekrar deneyin.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Firebase Auth hata kodlarını Türkçe mesaja çevirir; yoksa backend hatasına düşer.
export function formatAuthError(e) {
  const code = e?.code || "";
  const map = {
    "auth/invalid-email": "Geçersiz e-posta adresi.",
    "auth/user-not-found": "E-posta veya şifre hatalı.",
    "auth/wrong-password": "E-posta veya şifre hatalı.",
    "auth/invalid-credential": "E-posta veya şifre hatalı.",
    "auth/email-already-in-use": "Bu e-posta zaten kayıtlı.",
    "auth/weak-password": "Şifre en az 6 karakter olmalıdır.",
    "auth/too-many-requests": "Çok fazla deneme yapıldı. Lütfen biraz sonra tekrar deneyin.",
    "auth/network-request-failed": "Bağlantı hatası. İnternetinizi kontrol edin.",
    "auth/user-disabled": "Bu hesap devre dışı bırakılmış.",
  };
  if (code && map[code]) return map[code];
  if (e?.response?.data?.detail) return formatApiError(e.response.data.detail);
  return e?.message || "Bir hata oluştu. Lütfen tekrar deneyin.";
}
